import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ANALYSIS_IDS } from '../models/stylometric.model';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StylometricStateService {
  private reportTypeSubject = new BehaviorSubject<'classic' | 'stylometric'>('classic');
  reportType$ = this.reportTypeSubject.asObservable();

  private isStylometricReportOpenSubject = new BehaviorSubject<boolean>(false);
  isStylometricReportOpen$ = this.isStylometricReportOpenSubject.asObservable();

  private isProjectSynapseActiveSubject = new BehaviorSubject<boolean>(false);
  isProjectSynapseActive$ = this.isProjectSynapseActiveSubject.asObservable();

  private selectedAnalysisSubject = new BehaviorSubject<string>('');
  selectedAnalysis$ = this.selectedAnalysisSubject.asObservable();

  private previousSubmissionMetricsSubject = new BehaviorSubject<any>(null);
  previousSubmissionMetrics$ = this.previousSubmissionMetricsSubject.asObservable();

  private selectedSubmissionSubject = new BehaviorSubject<string>('all');
  selectedSubmission$ = this.selectedSubmissionSubject.asObservable();

  private availableAIModelsSubject = new BehaviorSubject<any[]>([]);
  availableAIModels$ = this.availableAIModelsSubject.asObservable();

  private selectedAIModelSubject = new BehaviorSubject<any>(null);
  selectedAIModel$ = this.selectedAIModelSubject.asObservable();

  private selectedModelPdfUrlSubject = new BehaviorSubject<string>('');
  selectedModelPdfUrl$ = this.selectedModelPdfUrlSubject.asObservable();

  private stylometricFlagsSubject = new BehaviorSubject<number>(0);
  stylometricFlags$ = this.stylometricFlagsSubject.asObservable();

  private projectSynapseLocked = false;

  constructor(private http: HttpClient) { }

  setReportType(type: 'classic' | 'stylometric') {
    // Store current Project Synapse state before changing report type
    const isProjectSynapseActive = this.isProjectSynapseActiveSubject.getValue();

    this.reportTypeSubject.next(type);

    if (type === 'stylometric') {
      this.setSelectedAnalysis(ANALYSIS_IDS.LEXICAL);

      if (!isProjectSynapseActive) {
        this.toggleStylometricReport(true);
      }
    } else {
      this.toggleStylometricReport(false);

      // Restore Project Synapse state after switching to classic mode
      if (isProjectSynapseActive) {
        // Use setTimeout to ensure this happens after other state changes
        setTimeout(() => {
          this.toggleProjectSynapseActive(true);
        }, 0);
      }
    }
  }

  toggleStylometricReport(isOpen: boolean) {
    this.isStylometricReportOpenSubject.next(isOpen);
    if (isOpen) {
      this.setSelectedAnalysis(ANALYSIS_IDS.LEXICAL);
    }
  }

  setSelectedAnalysis(analysis: string) {
    this.selectedAnalysisSubject.next(analysis);
  }

  setPreviousSubmissionMetrics(metrics: any) {
    this.previousSubmissionMetricsSubject.next(metrics);
  }

  setSelectedSubmission(submissionId: string) {
    this.selectedSubmissionSubject.next(submissionId);
  }

  toggleProjectSynapseActive(isActive: boolean): void {
    // Check if we're already in the desired state to avoid unnecessary updates
    if (this.isProjectSynapseActiveSubject.getValue() === isActive) {
      return;
    }

    if (this.projectSynapseLocked && !isActive) {
      return;
    }

    // When enabling Project Synapse, preserve current report type
    const currentReportType = this.reportTypeSubject.getValue();

    // Update the state
    this.isProjectSynapseActiveSubject.next(isActive);

    // Ensure the report type is preserved
    if (isActive && currentReportType === 'stylometric') {
      // Make sure we're still in stylometric mode if we were before
      setTimeout(() => {
        this.reportTypeSubject.next('stylometric');
      }, 0);
    }
  }

  // Add a method to force the Project Synapse state regardless of current state
  forceProjectSynapseState(isActive: boolean): void {
    // Temporarily unlock the state if it's locked
    const wasLocked = this.projectSynapseLocked;
    this.projectSynapseLocked = false;

    // Force the state update
    this.isProjectSynapseActiveSubject.next(isActive);

    // Restore the lock state
    this.projectSynapseLocked = wasLocked;
  }

  setSelectedModelPdfUrl(url: string) {
    this.selectedModelPdfUrlSubject.next(url);
  }

  setSelectedAIModel(model: any, skipHighlightUpdate: boolean = false): void {
    // Add the skipHighlightUpdate flag to the model object
    const modelWithFlag = {
      ...model,
      skipHighlightUpdate
    };

    this.selectedAIModelSubject.next(modelWithFlag);
  }

  getCurrentAIModel(): any {
    return this.selectedAIModelSubject.getValue();
  }

  getStylometricAnalysis(
    currentSubmission: any,
    previousSubmission: any,
    thresholds: any,
    isProjectSynapseActive: boolean = false,
    flaggedMetrics: any = null
  ) {
    return this.http.post<any>('https://ijoyxjl89a.execute-api.eu-central-1.amazonaws.com/prod/stylometric', {
      current_submission: currentSubmission,
      previous_submission: previousSubmission,
      thresholds: thresholds,
      is_project_synapse_active: isProjectSynapseActive,
      flagged_metrics: flaggedMetrics,
      isProjectSynapse: isProjectSynapseActive
    });
  }

  setStylometricFlags(count: number) {
    this.stylometricFlagsSubject.next(count);
  }

  // Function to exclude/include sections in project synapse
  toggleProjectSynapseSections(submissionId: string, modelName: string, sectionKey: string) {
    return this.http.post<any>(`${environment.apiUrl}/submissions/${submissionId}/ai-model-section/toggle`, {
      modelName: modelName,
      sectionKey: sectionKey,
      submissionId: submissionId
    });
  }

  lockProjectSynapseState(): void {
    this.projectSynapseLocked = true;
  }

  unlockProjectSynapseState(): void {
    this.projectSynapseLocked = false;
  }

  getProjectSynapseActive(): boolean {
    return this.isProjectSynapseActiveSubject.getValue();
  }

  setAvailableAIModels(models: any[]): void {
    this.availableAIModelsSubject.next(models);
  }

  getAvailableAIModels(): any[] {
    return this.availableAIModelsSubject.getValue();
  }

}
